import React from 'react'
import PropTypes from 'prop-types'
import { Container } from '../../core'
import { Columns, Column } from '../../bulma'
import { helpers } from '../../../helpers'
import ProductSearchHeader from './ProductSearchHeader'
import SearchFeedback from './SearchFeedback'
import ProductGrid from '../ProductGrid'
import LoadMoreProducts from './LoadMoreProducts'
import SidebarFilters from './SidebarFilters'
import MobileFiltersContainer from './MobileFiltersContainer'
import { RATING_FILTER, PRODUCER_FILTER, TAXON_FILTER, MIN_PRICE_FILTER, MAX_PRICE_FILTER, SUBSCRIPTION_PLAN_FILTER, ON_SALE_FILTER } from './constants'

/**
* Container component for displaying products and filter options.
*/
class ProductSearchContainer extends React.Component {
  constructor(props) {
    super(props)

    // Determine which filters are currently applied.
    const url = new Url(`/products.json?${props.filters}`)
    const filters = url.query

    filters.keywords = props.keywords

    this.state = {
      products: helpers.formatProducts(props.products),
      filters,
      aggregations: props.aggregations,
      metadata: props.metadata,
      subscriptions: props.subscriptions,
      isLoading: false,
    }

    this.onPopstateHandler = this.onPopstateHandler.bind(this)
    this.filterChangeHandler = this.filterChangeHandler.bind(this)
    this.loadMoreHandler = this.loadMoreHandler.bind(this)
    this.getFilterChangeUrl = this.getFilterChangeUrl.bind(this)
    this.getListName = this.getListName.bind(this)
    this.formatUrl = this.formatUrl.bind(this)
    this.getProducts = this.getProducts.bind(this)
  }

  componentDidMount() {
    window.onpopstate = this.onPopstateHandler
  }

  /**
  * onPopstate handler. This parses the filters in the current url and
  * updates the list of products. This is so that the back button works as
  * expected.
  */
  onPopstateHandler() {
    // Grab the new filters based on the url.
    const url = new Url()

    this.setState({ filters: url.query })
    // Fetch new products.
    this.getProducts(url.query, true)
  }

  /**
  * Generic callback for filter changing components. Takes in an object of
  * filters/params to be modified from the current filters. Once the filters are
  * updated, the url is updated if possible, and then a new list of products
  * is requested from the server using the updated filters.
  *
  * @param filters {object} changes made to state's filters.
  */
  filterChangeHandler(filters) {
    // Update filter state.
    const newFilters = helpers.clean(Object.assign(
      {},
      this.state.filters,
      filters,
    ))

    this.setState({ filters: newFilters })

    // Update url if possible.
    if (history.pushState) {
      history.pushState(null, null, this.formatUrl(null, newFilters))
    }

    // Fetch new products.
    this.getProducts(newFilters, true)
  }

  /**
  * onClick event wrapper that fetches more products and tracks the event.
  */
  loadMoreHandler(e) {
    e.preventDefault()
    $.track('event', 'Search', 'Load More Products', this.state.metadata.nextPage)
    this.getProducts(this.state.filters, false)
  }

  /**
  * Returns what the url will be if given changes are applied.
  *
  * @param changes {object} Hypothetical changes to filters.
  * @returns {string} Hypothetical url.
  */
  getFilterChangeUrl(changes) {
    // Update filter state.
    const newFilters = helpers.clean(Object.assign(
      {},
      this.state.filters,
      changes,
    ))

    return this.formatUrl(null, newFilters)
  }

  /**
  * Returns the current analytics list name based on current filters.
  *
  * @returns {string}
  */
  getListName() {
    const listName = ['Grid']

    listName.push(this.props.title ? this.props.title : 'Search')

    // Taxon Filters
    if (TAXON_FILTER in this.state.filters) {
      if (Array.isArray(this.state.filters[TAXON_FILTER])) {
        listName.push(`Taxon: ${this.state.filters[TAXON_FILTER].join(', ')}`)
      } else {
        listName.push(`Taxon: ${this.state.filters[TAXON_FILTER]}`)
      }
    }

    // Producer Filters
    if (PRODUCER_FILTER in this.state.filters) {
      if (Array.isArray(this.state.filters[PRODUCER_FILTER])) {
        listName.push(`Author: ${this.state.filters[PRODUCER_FILTER].join(', ')}`)
      } else {
        listName.push(`Author: ${this.state.filters[PRODUCER_FILTER]}`)
      }
    }

    // Subscription Filters
    if (SUBSCRIPTION_PLAN_FILTER in this.state.filters) {
      listName.push(`Subscription: ${this.state.filters[SUBSCRIPTION_PLAN_FILTER]}`)
    }

    // Promotion Filters
    if (ON_SALE_FILTER in this.state.filters) {
      listName.push(`On Sale: ${this.state.filters[ON_SALE_FILTER]}`)
    }

    // Price Filters
    if (MIN_PRICE_FILTER in this.state.filters || MAX_PRICE_FILTER in this.state.filters) {
      const prices = [
        MIN_PRICE_FILTER in this.state.filters ? this.state.filters[MIN_PRICE_FILTER] : '0',
        MAX_PRICE_FILTER in this.state.filters ? this.state.filters[MAX_PRICE_FILTER] : 'above',
      ]

      listName.push(`Price: ${prices.join(' - ')}`)
    }

    // Rating Filters
    if (RATING_FILTER in this.state.filters) {
      listName.push(`Rating: ${this.state.filters[RATING_FILTER]}`)
    }

    return listName.join(' | ')
  }

  /**
  * Helper function that properly applies parameters to a url and then returns
  * the update url string.
  *
  * @param path {string}
  * @param params {object} query params to apply to path.
  *
  * @returns {string}
  */
  formatUrl(path, params = {}) {
    const url = new Url(path)

    url.clearQuery()

    // Add in each param
    for (const key in params) {
      url.query[key] = params[key]
    }

    return url.decode(url.toString())
  }

  /**
  * Fetches a new/updated list of products based on the filters received. If
  * `refresh` param is set to `true`, then the list of products will be replaced
  * by the list of products received from the server, if it's `false` then the
  * results will be appended to the current list of products.
  *
  * @param filters {object} filters/query params to apply to search request.
  * @param refresh {bool} true if list of results should be replaced.
  */
  getProducts(filters, refresh = false) {
    this.setState({ isLoading: true, filters })
    // Determine page number to request.

    // Add additional parameters that shouldn't be shown in the browser history.
    const params = Object.assign({}, filters, {
      page: refresh ? 1 : this.state.metadata.nextPage,
      per_page: 48,
    })

    const path = this.formatUrl('/products.json', params)

    debug.data('GET', path)
    debug.performanceStart(path)
    $.getJSON(path)
      .done((data) => {
        debug.performanceEnd(path)
        debug.data('SUCCESS', data)
        const products = helpers.formatProducts(data.data.products)


        const updatedState = {
          products: refresh ? products : this.state.products.concat(products),
          aggregations: data.data.aggregations,
          metadata: data.data.metadata,
          isLoading: false,
        }

        this.setState(updatedState)
      })
      .fail((data) => {
        console.error(data)
        this.setState({
          error: true,
          isLoading: false,
        })
      })
  }

  render() {
    return (
      <Container>
        <ProductSearchHeader
          current={this.state.products.length}
          total={this.state.metadata.totalCount}
          filters={this.state.filters}
        />
        <Columns>
          <Column className="is-4-tablet is-3-desktop">
            <MobileFiltersContainer
              aggregations={this.state.aggregations}
              filters={this.state.filters}
              getFallbackUrl={this.getFilterChangeUrl}
              onChange={this.filterChangeHandler}
              isLoading={this.state.isLoading}
            />
            <SidebarFilters
              className="is-hidden-mobile"
              aggregations={this.state.aggregations}
              filters={this.state.filters}
              getFallbackUrl={this.getFilterChangeUrl}
              onChange={this.filterChangeHandler}
              isLoading={this.state.isLoading}
            />
          </Column>
          <Column>
            <ProductGrid
              listName={this.getListName()}
              products={this.state.products}
              isLoading={this.state.isLoading}
            />
            <LoadMoreProducts
              isLoading={this.state.isLoading}
              canLoadMore={!this.state.metadata.isLastPage && Boolean(this.state.metadata.nextPage)}
              fallbackUrl={this.getFilterChangeUrl({ page: this.state.metadata.nextPage })}
              hasError={this.state.error}
              onClick={this.loadMoreHandler}
            />
            <SearchFeedback />
          </Column>
        </Columns>
      </Container>
    )
  }
}

ProductSearchContainer.defaultProps = {
  aggregations: {},
  filters: '',
  keywords: '',
  metadata: {},
  products: [],
  title: '',
}

ProductSearchContainer.propTypes = {
  // Search aggregations
  aggregations: PropTypes.object,
  // string of url query params
  filters: PropTypes.string,
  // keywords query param
  keywords: PropTypes.string,
  // Pagination and results related data.
  metadata: PropTypes.object,
  // List of visible products
  products: PropTypes.array,
  // Page display name
  title: PropTypes.string,
}

export default ProductSearchContainer
